/**
 * @fileoverview Minimal WebZjs Keys-and-Send Client (shared)
 *
 * Wraps the webzjs-keys-and-send WASM package for:
 *   - Key derivation (USK, UFVK, seed fingerprint)
 *   - PCZT signing
 *   - Seed phrase generation & address validation
 *
 * The WASM module itself is injected at runtime via `configureWasm()` so
 * that both WINBIT32 and NFPT can bring their own build artefacts.
 *
 * Ported from WINBIT32 webzjs-keys-and-send-client-minimal.ts
 */

import { WASM_KEYS_AND_SEND_PATH } from './constants';
import type { ZcashNetwork, ZcashAddressValidation } from './types';

// ---------------------------------------------------------------------------
// WASM type abstractions
// ---------------------------------------------------------------------------

/**
 * Opaque handle representing a Zcash Unified Spending Key.
 * The concrete type comes from the WASM module at runtime.
 */
export interface IUnifiedSpendingKey {
	to_unified_full_viewing_key(): IUnifiedFullViewingKey;
	seed_fingerprint(): ISeedFingerprint;
}

export interface IUnifiedFullViewingKey {
	to_unified_address(network: string, diversifierIndex: number): string;
}

export interface ISeedFingerprint {
	to_bytes(): Uint8Array;
}

export interface IPczt {
	serialize(): Uint8Array;
}

/** Static constructor on the Pczt class */
export interface IPcztStatic {
	from_bytes(bytes: Uint8Array): IPczt;
}

/** Shape of the WASM module's exports that we consume */
export interface WebZjsWasmModule {
	default: (pathOrBuffer: string | ArrayBuffer) => Promise<void>;
	UnifiedSpendingKey: any;
	derive_usk_from_mnemonic: (network: string, mnemonic: string, accountIndex: number) => IUnifiedSpendingKey;
	ufvk_from_usk: (usk: IUnifiedSpendingKey, network: string) => string;
	pczt_sign: (network: string, pczt: IPczt, usk: IUnifiedSpendingKey, seedFp: ISeedFingerprint) => Promise<IPczt>;
	Pczt: IPcztStatic;
	generate_seed_phrase: () => string;
	SeedFingerprint: new (seed: Uint8Array) => ISeedFingerprint;
}

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------

let wasmModule: WebZjsWasmModule | null = null;
let wasmInitialized = false;
let initializationPromise: Promise<boolean> | null = null;

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/**
 * Provide the WASM module exports so the client can call them.
 *
 * ```ts
 * import * as wasmExports from './webzjs_keys_and_send_fixed';
 * configureWasm(wasmExports);
 * ```
 */
export function configureWasm(mod: WebZjsWasmModule): void {
	wasmModule = mod;
	// Reset init state when a new module is configured
	wasmInitialized = false;
	initializationPromise = null;
}

function requireWasmModule(): WebZjsWasmModule {
	if (!wasmModule) {
		throw new Error(
			'WebZjs WASM module not configured. Call configureWasm(module) before using the client.',
		);
	}
	return wasmModule;
}

// ---------------------------------------------------------------------------
// WASM initialisation
// ---------------------------------------------------------------------------

async function initializeWasm(wasmPath?: string): Promise<boolean> {
	if (wasmInitialized) return true;
	if (initializationPromise) return initializationPromise;

	const mod = requireWasmModule();

	initializationPromise = (async () => {
		try {
			const path = wasmPath ?? WASM_KEYS_AND_SEND_PATH;
			console.log(`🔧 Initializing WebZjs Keys-and-Send WASM from ${path}...`);
			await mod.default(path);
			wasmInitialized = true;
			console.log('✅ WebZjs Keys-and-Send WASM initialized successfully');
			return true;
		} catch (error) {
			console.error('❌ WebZjs Keys-and-Send WASM initialization failed:', error);
			wasmInitialized = false;
			throw error;
		}
	})();

	return initializationPromise;
}

// ---------------------------------------------------------------------------
// Client class
// ---------------------------------------------------------------------------

export class WebZjsKeysAndSendClient {
	private network: string;
	private lightwalletdUrl: string;

	constructor(
		network: string = 'main',
		lightwalletdUrl: string = 'https://lightwalletd.zcash.winbit32.com',
	) {
		this.network = network;
		this.lightwalletdUrl = lightwalletdUrl;
	}

	/** Initialize the client (loads WASM if not yet loaded). */
	async initialize(): Promise<void> {
		await initializeWasm();
		console.log(`✅ WebZjs client initialized for network: ${this.network}`);
	}

	/**
	 * Derive a unified address, USK, UFVK, and seed fingerprint from mnemonic.
	 */
	async generateUnifiedAddress(
		mnemonic: string,
		accountIndex: number = 0,
	): Promise<{
		address: string;
		usk: IUnifiedSpendingKey;
		ufvk: string;
		seedFp: ISeedFingerprint;
	}> {
		await this.initialize();
		const mod = requireWasmModule();

		console.log(`🔑 Deriving unified address for account ${accountIndex}...`);

		// We only need the seed for the SeedFingerprint constructor;
		// the actual key derivation uses the mnemonic directly.
		// Import bip39 lazily to keep this module lightweight when the
		// consumer already provides their own mnemonic-to-seed logic.
		let mnemonicToSeedSync: (m: string) => Uint8Array;
		try {
			// eslint-disable-next-line @typescript-eslint/no-var-requires
			const bip39 = await import('@scure/bip39');
			mnemonicToSeedSync = (m: string) => new Uint8Array(bip39.mnemonicToSeedSync(m));
		} catch {
			throw new Error('@scure/bip39 is required for seed fingerprint derivation');
		}

		const seed = mnemonicToSeedSync(mnemonic);
		const seedFp = new mod.SeedFingerprint(seed);

		const usk = mod.derive_usk_from_mnemonic(this.network, mnemonic, accountIndex);
		const ufvkEncoded = mod.ufvk_from_usk(usk, this.network);

		const ufvkObj = usk.to_unified_full_viewing_key();
		const address = ufvkObj.to_unified_address(this.network, 0);

		console.log(`✅ Unified address generated: ${String(address).substring(0, 20)}...`);

		return { address: String(address), usk, ufvk: ufvkEncoded, seedFp };
	}

	/** Export Unified Full Viewing Key (UFVK). */
	async exportUnifiedFullViewingKey(
		mnemonic: string,
		params?: { account?: number },
	): Promise<string> {
		await this.initialize();
		const mod = requireWasmModule();

		const accountIndex = params?.account ?? 0;
		const usk = mod.derive_usk_from_mnemonic(this.network, mnemonic, accountIndex);
		return mod.ufvk_from_usk(usk, this.network);
	}

	/** Sign a PCZT with a USK. */
	async signPczt(pczt: IPczt, usk: IUnifiedSpendingKey): Promise<IPczt> {
		await this.initialize();
		const mod = requireWasmModule();

		const seedFp = usk.seed_fingerprint();
		return mod.pczt_sign(this.network, pczt, usk, seedFp);
	}

	/** Generate a new BIP-39 seed phrase. */
	static generateSeedPhrase(): string {
		const mod = requireWasmModule();
		return mod.generate_seed_phrase();
	}

	/** Simple address validation based on prefix. */
	async validateAddress(address: string): Promise<ZcashAddressValidation> {
		if (address.startsWith('u1')) {
			return { isValid: true, type: 'unified' };
		} else if (address.startsWith('zs1') || address.startsWith('ztestsapling')) {
			return { isValid: true, type: 'sapling' };
		} else if (address.startsWith('t1') || address.startsWith('t3')) {
			return { isValid: true, type: 'transparent' };
		}
		return { isValid: false, type: 'unknown' };
	}

	/** @deprecated Use lightwalletd or RPC directly. */
	async getBalance(_address: string): Promise<{ balanceZec: number }> {
		console.warn('⚠️ getBalance is deprecated – use lightwalletd or RPC directly');
		return { balanceZec: 0 };
	}

	/** @deprecated Use PCZT builder API instead. */
	async sendTransaction(_params: any): Promise<any> {
		throw new Error('sendTransaction is deprecated – use PCZT builder API instead');
	}
}

// ---------------------------------------------------------------------------
// Singleton cache
// ---------------------------------------------------------------------------

const clientCache = new Map<string, WebZjsKeysAndSendClient>();

/** Get or create a cached WebZjs client instance. */
export function getWebZjsClient(
	network: string = 'main',
	lightwalletdUrl?: string,
): WebZjsKeysAndSendClient {
	const cacheKey = `${network}:${lightwalletdUrl || 'default'}`;
	if (!clientCache.has(cacheKey)) {
		clientCache.set(cacheKey, new WebZjsKeysAndSendClient(network, lightwalletdUrl));
	}
	return clientCache.get(cacheKey)!;
}

// ---------------------------------------------------------------------------
// Convenience exports
// ---------------------------------------------------------------------------

export const initializeWebZjs = async (wasmPath?: string): Promise<void> => {
	await initializeWasm(wasmPath);
};

export const isWebZjsInitialized = (): boolean => wasmInitialized;

export const generateWebZjsSeedPhrase = (): string =>
	WebZjsKeysAndSendClient.generateSeedPhrase();

/**
 * Access the raw Pczt constructor from the configured WASM module
 * (needed by shieldedTransactionClient for `Pczt.from_bytes()`).
 */
export function getPcztClass(): IPcztStatic {
	return requireWasmModule().Pczt;
}

/**
 * Access the raw pczt_sign function from the configured WASM module.
 */
export function getPcztSign(): WebZjsWasmModule['pczt_sign'] {
	return requireWasmModule().pczt_sign;
}

